import { crossCategoryLinkSelector } from '@client/redux/experiments/selectors/cross-category-link';
import { markAsBookedLiteStorefrontsSelector } from '@client/redux/experiments/selectors/mark-as-booked-lite-storefronts';
import { newCTASelector } from '@client/redux/experiments/selectors/new-cta';
import { weddingsHostedCountBadgeAssignmentSelector } from '@client/redux/experiments/selectors/weddings-hosted-count-badge';
import { reportServerSideExperiment } from '@redux/experiments';
import { rfqCtaCopyVenuesSelector } from '@redux/experiments/selectors/rfq-cta-copy-venues';
import { storefrontCollapsedRfqSelector } from '@redux/experiments/selectors/storefront-collapsed-rfq';
import { storefrontPeaceOfMindSelector } from '@redux/experiments/selectors/storefront-peace-of-mind';
import { storefrontPeaceOfMindRetestSelector } from '@redux/experiments/selectors/storefront-peace-of-mind-retest';
import { storefrontPeaceOfMindRetestPartTwoSelector } from '@redux/experiments/selectors/storefront-peace-of-mind-retest-part-two';
import { storefrontsNetworkAssignmentSelector } from '@redux/experiments/selectors/storefronts-network';
import { typeaheadTypoToleranceSelector } from '@redux/experiments/selectors/typeahead-typo-tolerance';
import { userEngagementInteractivityAssignmentSelector } from '@redux/experiments/selectors/user-engagement-interactivity';
import { useAppDispatch, useAppSelector } from '@redux/hooks';
import { experiments } from '@settings';
import { Logger } from '@utils/logger/logger';
import { useCallback, useEffect } from 'react';
import type { Id } from 'types/experiments';

export const useStorefrontExperiments = () => {
	const dispatch = useAppDispatch();

	const userEngagementInteractivityAssignmentVariant = useAppSelector((state) =>
		userEngagementInteractivityAssignmentSelector(state),
	);

	const storefrontsNetworkVariant = useAppSelector((state) =>
		storefrontsNetworkAssignmentSelector(state),
	);

	const storefrontPeaceOfMindAssignment = useAppSelector(
		storefrontPeaceOfMindSelector,
	);

	const storefrontPeaceOfMindRetestAssignment = useAppSelector(
		storefrontPeaceOfMindRetestSelector,
	);

	const newCTAAssignment = useAppSelector((state) => newCTASelector(state));

	const storefrontCollapsedRfqAssignment = useAppSelector((state) =>
		storefrontCollapsedRfqSelector(state),
	);

	const storefrontPeaceOfMindRetestPartTwoAssignment = useAppSelector(
		storefrontPeaceOfMindRetestPartTwoSelector,
	);

	const typeaheadTypoToleranceAssignment = useAppSelector((state) =>
		typeaheadTypoToleranceSelector(state),
	);

	const weddingsHostedCountBadgeAssignment = useAppSelector((state) =>
		weddingsHostedCountBadgeAssignmentSelector(state),
	);

	const markAsBookedLiteStorefrontsAssignment = useAppSelector(
		markAsBookedLiteStorefrontsSelector,
	);
	const rfqCtaCopyVenuesAssignment = useAppSelector(rfqCtaCopyVenuesSelector);

	const crossCategoryLinkAssignment = useAppSelector(crossCategoryLinkSelector);

	const reportExperiment = useCallback(
		(experimentId: Id) => {
			dispatch(reportServerSideExperiment(experimentId));
		},
		[dispatch],
	);

	// biome-ignore lint/correctness/useExhaustiveDependencies: onMount
	useEffect(() => {
		if (userEngagementInteractivityAssignmentVariant) {
			Logger.log({
				log_message: `User Engagement Interactivity assignment:  ${userEngagementInteractivityAssignmentVariant}`,
			});
			reportExperiment(experiments.userEngagementInteractivity);
		}
	}, [userEngagementInteractivityAssignmentVariant]);

	useEffect(() => {
		if (storefrontsNetworkVariant) {
			Logger.log({
				log_message: `Storefronts Network Assignment: ${storefrontsNetworkVariant}`,
			});
			reportExperiment(experiments.storefrontsNetwork);
		}
	}, [reportExperiment, storefrontsNetworkVariant]);

	useEffect(() => {
		if (storefrontPeaceOfMindAssignment) {
			Logger.log({
				log_message: `Storefront Peace of Mind Assignment: ${storefrontPeaceOfMindAssignment}`,
			});
			reportExperiment(experiments.storefrontPeaceOfMind);
		}
	}, [reportExperiment, storefrontPeaceOfMindAssignment]);

	useEffect(() => {
		if (storefrontPeaceOfMindRetestAssignment) {
			Logger.log({
				log_message: `Storefront Peace of Mind Retest Assignment: ${storefrontPeaceOfMindRetestAssignment}`,
			});
			reportExperiment(experiments.storefrontPeaceOfMindRetest);
		}
	}, [reportExperiment, storefrontPeaceOfMindRetestAssignment]);

	useEffect(() => {
		if (newCTAAssignment) {
			Logger.log({
				log_message: `New CTA Assignment: ${newCTAAssignment}`,
			});
			reportExperiment(experiments.newCTA);
		}
	}, [reportExperiment, newCTAAssignment]);

	useEffect(() => {
		if (storefrontCollapsedRfqAssignment) {
			Logger.log({
				log_message: `Storefront Collapsed RFQ Assignment: ${storefrontCollapsedRfqAssignment}`,
			});
			reportExperiment(experiments.storefrontCollapsedRfq);
		}
	}, [reportExperiment, storefrontCollapsedRfqAssignment]);

	useEffect(() => {
		if (storefrontPeaceOfMindRetestPartTwoAssignment) {
			Logger.log({
				log_message: `Storefront Peace of Mind Retest PART TWO Assignment: ${storefrontPeaceOfMindRetestPartTwoAssignment}`,
			});
			reportExperiment(experiments.storefrontPeaceOfMindRetestPartTwo);
		}
	}, [reportExperiment, storefrontPeaceOfMindRetestPartTwoAssignment]);

	useEffect(() => {
		if (typeaheadTypoToleranceAssignment) {
			Logger.log({
				log_message: `Typeahead Typo Tolerance Assignment: ${typeaheadTypoToleranceAssignment}`,
			});
			reportExperiment(experiments.typeaheadTypoTolerance);
		}
	}, [reportExperiment, typeaheadTypoToleranceAssignment]);

	useEffect(() => {
		if (weddingsHostedCountBadgeAssignment) {
			Logger.log({
				log_message: `Weddings Hosted Count Badge Assignment: ${weddingsHostedCountBadgeAssignment}`,
			});
			reportExperiment(experiments.weddingsHostedCountBadge);
		}
	}, [reportExperiment, weddingsHostedCountBadgeAssignment]);

	useEffect(() => {
		if (markAsBookedLiteStorefrontsAssignment) {
			Logger.log({
				log_message: `Mark As Booked Lite Storefronts Assignment: ${markAsBookedLiteStorefrontsAssignment}`,
			});
			reportExperiment(experiments.markAsBookedLiteStorefronts);
		}
	}, [reportExperiment, markAsBookedLiteStorefrontsAssignment]);

	useEffect(() => {
		if (rfqCtaCopyVenuesAssignment) {
			Logger.log({
				log_message: `RFQ CTA Copy Venues Assignment: ${rfqCtaCopyVenuesAssignment}`,
			});
			reportExperiment(experiments.rfqCtaCopyVenues);
		}
	}, [reportExperiment, rfqCtaCopyVenuesAssignment]);

	useEffect(() => {
		if (crossCategoryLinkAssignment) {
			Logger.log({
				log_message: `Cross Category Link Assignment: ${crossCategoryLinkAssignment}`,
			});
			reportExperiment(experiments.crossCategoryLink);
		}
	}, [reportExperiment, crossCategoryLinkAssignment]);

	return {
		userEngagementInteractivityAssignmentVariant,
		storefrontsNetworkVariant,
		storefrontPeaceOfMindAssignment,
		storefrontPeaceOfMindRetestAssignment,
		newCTAAssignment,
		storefrontCollapsedRfqAssignment,
		storefrontPeaceOfMindRetestPartTwoAssignment,
		typeaheadTypoToleranceAssignment,
		weddingsHostedCountBadgeAssignment,
		markAsBookedLiteStorefrontsAssignment,
		crossCategoryLinkAssignment,
	};
};
